import config from "appConfig";

const theory = ` <h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>
   This lesson covers the normal and oblique incidence of plane waves on a wall or some other type of obstacle such as a plate, with regard to the parameters that describe the sound field in front of the wall and behind it, namely sound pressure and sound power. The definitions will be given for the reflection coefficients
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   , and for the transmission coefficients
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
   </math>
   , derived for sound pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   and sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   , respectively. Characteristic acoustic impedances
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
   </math>
   of media will be used, and the connection will be made with the most important parameter in building acoustics: the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   .<br class=newline>The mass law will be included in the considerations of single leaf constructions, both with and without the coincidence and resonance effects that appear due to the propagation of flexural waves in plates and similar structures. At the end, the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   derived from the power transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
   </math>
   will be defined for composite structures such as a wall with doors, windows, or some other type of elements with known sound reduction coefficient.
<h3 class=sectionHead>Introduction</h3>
<p>
   This lesson discusses the reflection, transmission and absorption of sound waves through a wall in the audible frequency range by using sound power considerations. The block diagram of the lesson is shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/17/theory/figure-1.svg" data-img-alt="Figure 1. Block scheme of the lesson.">Figure 1</a>. Each building element has its own sound reduction index, and the overall sound reduction index is determined by simplified sound power considerations.
   <figure class=figure>
<p><img alt="Figure 1. Block scheme of the lesson." src="${config.ASSETS_URL}/05/17/theory/figure-1.svg"><figcaption class=caption>Figure 1. Block scheme of the lesson.</figcaption></figure>
<p>
   When a sound wave hits a building structure, there are several processes that should be considered. Firstly, there is the total incident energy which can be expressed either with sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   or with intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
   </math>
   , which depends on the type of the sound field in front of the building structure (free field, diffuse field, plane wave, spherical wave, cylindrical wave).<br class=newline>
<p>According to <a href="#" data-img-alt="Figure 2. The energy balance of sound incident on a partition, with all effects included." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-2.svg>Figure 2</a>, the energy balance can be described with the following components [1]:
<ol class=enumerate1>
   <li id=x1-2004x1 class=enumerate>
      Total sound energy incident on the partition, expressed with incident sound power
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </math>
   <li id=x1-2006x2 class=enumerate>
      Part of the energy reflected from the partition due to discontinuity between the two media, expressed with reflected sound power
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>
   <li id=x1-2008x3 class=enumerate>
      Part of the energy reflected back due to structural vibrations of partition - re-radiated energy, expressed with reflected sound power
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mn>3</mn>
            </mrow>
         </msub>
      </math>
   <li id=x1-2010x4 class=enumerate>
      Part of the energy transmitted to the opposite side of the partition, due to longitudinal and bending waves in the structure, expressed with the transmitted sound power
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mn>4</mn>
            </mrow>
         </msub>
      </math>
   <li id=x1-2012x5 class=enumerate>
      Part of the energy transmitted to the opposite side of the partition, due to the porous structure of the partition, expressed with the transmitted sound power
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mn>5</mn>
            </mrow>
         </msub>
      </math>
   <li id=x1-2014x6 class=enumerate>
      Part of the energy absorbed by the partition and converted into heat, expressed with the absorbed sound power
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mn>6</mn>
            </mrow>
         </msub>
      </math>
   <li id=x1-2016x7 class=enumerate>
      Part of the energy transmitted to other connected partitions, expressed with the absorbed sound power
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mn>7</mn>
            </mrow>
         </msub>
      </math>
</ol>
<figure class=figure>
   <p>
      <img alt="Figure 2. The energy balance of sound incident on a partition, with all effects included." src=${config.ASSETS_URL}/05/17/theory/figure-2.svg>
      <figcaption class=caption>Figure 2. The energy balance of sound incident on a partition, with all effects included.</figcaption>
</figure>
<p>
   According to the energy preservation law, the incident acoustic energy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>W</mi>
   </math>
   expressed with incident sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is divided between reflected, transmitted and absorbed acoustic energy as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>3</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>5</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>6</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>7</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   The part of the sound energy that is reflected from the partition can be determined by knowing the sound power reflection coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   , which is generally given as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>r</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   The sound power reflection coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   can also be determined from other sound field parameters, namely sound pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   and particle velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
   </math>
   , both of which depend on the type of sound field in front of the partition (free or diffuse field). The sound pressure reflection coefficient for normal and oblique incidence, and for arbitrary and harmonic excitation signals is described in chapters 4 and 5.<br class=newline>
<p>Based on the diagram shown in <a href="#" data-img-alt="Figure 2. The energy balance of sound incident on a partition, with all effects included." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-2.svg>Figure 2</a>, the sound power absorption coefficient can be given as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>α</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>6</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>7</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>The sound power transmitted through the partition is determined by the sound power transmission coefficient, which is given as [4]:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>τ</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>5</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>The values of the reflection, absorption and transmission coefficients based on sound power all fall into the range between 0 and 1. Obeying the energy preservation law, the relationship between these three coefficients is given as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>r</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>α</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>τ</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   As the values of the sound transmission coefficient are very small for virtually all types of partitions available in buildings, it is much more practical to use the
   <strong>
      <span class=cmbx-10>sound reduction </span><span class=cmbx-10>index </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>R</mi>
      </math>
   </strong>
   expressed in decibels (dB) as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>τ</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   The sound Reduction Index (R). is a dimensionless parameter. Its value strongly depends on frequency due to the mass law, as well as resonance and coincidence effects, all of which define the sound insulation of any considered partition (a wall, a panel, a door, a window, etc.).<br class=newline>For partitions commonly found in buildings (walls, doors, windows, facades), the values of the sound transmission loss are considerably smaller than 1, and can be found in the range between
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>6</mn>
         </mrow>
      </msup>
   </math>
   . A typical value at 500 Hz is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math>
   , which in turn yields the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   equal to 50 dB.
<h3 class=sectionHead>Mass law for single-leaf constructions at normal and oblique incidence</h3>
<h4 class=subsectionHead>Normal incidence</h4>
<p>
   This section shows the derivation of the mass law under the assumption that a wall with mass
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   has inherent stiffness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   and damping
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   , which stems from the fact that a wall fixed at its edges acts like a string when its midsection is displaced. The model used in these considerations is shown in <a href="#" data-img-alt="Figure 3. Lumped element model for sound transmission through a single-leaf construction at normal incidence." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-3.svg>Figure 3</a>. The incident, reflected and transmitted sound pressure and particle velocity are shown as well.
   <figure class=figure>
<p><img alt="Figure 3. Lumped element model for sound transmission through a single-leaf construction at normal incidence." src=${config.ASSETS_URL}/05/17/theory/figure-3.svg><figcaption class=caption>Figure 3. Lumped element model for sound transmission through a single-leaf construction at normal incidence.</figcaption></figure>
<p>
   The connection between the transmitted pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and the incident pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be found by applying Newton’s law for the motion of the wall, as shown in Eq. 7  [2]. Assuming arbitrary excitation (including harmonic excitation), the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   can be calculated from basic material parameters. In addition, the particle velocities of the incident sound wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , the reflected sound wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , and the transmitted sound wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are considered in derivation of sound reduction index, as well as the vibration velocity of the wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   .
<table class=equation>
   <tr>
      <td style="font-size: 13px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mover accent=true>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>u</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>˙</mo>
            </mover>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>ξ</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>S</mi>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   In Eq. 7,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   is the vibration velocity of the wall,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ξ</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   is the displacement of the wall, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is the surface area of the wall. In the first approximation, it can be assumed that the surface area of the wall is very large.<br class=newline>The displacement of the wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ξ</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   can be expressed using the vibration velocity of the wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   , as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>ξ</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo>∫</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mi mathvariant=italic>dt</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   Eq. 7 can be divided with the surface area of the wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   , thus transforming the mass, stiffness and damping of the wall to their equivalent quantities expressed per unit area, i.e.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi>S</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi>S</mi>
   </math>
   , and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi>W</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi>S</mi>
   </math>
   .<br class=newline>
<p>The equation of 1-D motion of the wall is then given as
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>m</mi>
            <mover accent=true>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>u</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>˙</mo>
            </mover>
            <mo class=MathClass-bin>+</mo>
            <mi>r</mi>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>k</mi>
            <mo>∫</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mi mathvariant=italic>dt</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>The second boundary condition is given for the particle velocity, and it is the same for the interface of infinite dimensions.
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(10)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(11)
</table>
<p>For harmonic excitation, the pressure and particle velocity can be written in the form of phasors, as:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-punc>;</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-punc>;</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(12)
</table>
<table class=equation>
   <tr>
      <td style="font-size: 11px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-punc>;</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-punc>;</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(13)
</table>
<p>In this case, a steady-state solution is obtained for the differential equation, with relations between transmitted and incident pressures.
<table class=equation>
   <tr>
      <td style="font-size: 15px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>jωm</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>r</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>k</mi>
            <mo class=MathClass-bin>∕</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>jω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-close>)</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(14)
</table>
<p>The connection between the vibration velocity of the wall and the transmitted sound pressure is obtained using acoustic impedance of medium 1, as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>tr</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   The specific acoustic impedance of the wall (all parameters are divided with the surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   ) is given as
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>jωm</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>r</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>k</mi>
            <mo class=MathClass-bin>∕</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>jω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>r</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>j</mi>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>ωm</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>k</mi>
            <mo class=MathClass-bin>∕</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(16)
</table>
<p>
   The pressure transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   and the transmission loss (TL). (or the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   ) connected with sound power reduction can be found by observing squared pressure magnitudes of transmitted and incident sound:
<table class=equation>
   <tr>
      <td style="font-size: 6px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>T</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>r</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωm</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>k</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>r</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mo class=MathClass-open>(</mo>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jωm</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mo class=MathClass-open>(</mo>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <mi>ω</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(17)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi mathvariant=italic>TL</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>T</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(18)
</table>
<p>
   The angular resonance frequency of the wall is expressed as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>
   , with the corresponding linear frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi>π</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   .<br class=newline>
<p>
   According to the properties of classical materials used in building acoustics, the value of wall and panel stiffness (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   ) is very large due to the very large value of Young’s modulus of elasticity. Consequently, the resonance frequency (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   ) should fall in the kHz range. This means that the mass law is valid in this upper frequency range, as shown in <a href="#" data-img-alt="Figure 4. Comparison of sound reduction index for cement wall and wood panel." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-4.svg>Figure 4</a>. The figure shows the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   for a cement panel and a wooden panel according to the mass law, with stiffness and losses taken into account.
   <figure class=figure>
<p><img alt="Figure 4. Comparison of sound reduction index for cement wall and wood panel." src=${config.ASSETS_URL}/05/17/theory/figure-4.svg><figcaption class=caption>Figure 4. Comparison of sound reduction index for cement wall and wood panel.</figcaption></figure>
<p>
   When equation of motion for normal incidence is considered in more detail, we can assume that the wall velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   is very small. In this case, the term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ξ</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
      <mi>K</mi>
   </math>
   is negligible, as well as the loss term. In some literature, these terms are immediately neglected in the derivation of the mass law.<br class=newline>If the losses
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   are much lower than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>2</mn>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   ), the pressure transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   on the resonance frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is equal to 1, and the pressure reflection coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   is equal to zero.<br class=newline>
<p>
   In some derivations of the mass law, the resistance is assumed to be very small (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>2</mn>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   ), and the effective stiffness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   per unit area is also assumed to be very small (this is not necessarily correct). In this case, the resonance frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   falls below the frequency range of interest, and the transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   can be approximated as:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>T</mi>
            <mo class=MathClass-rel>≈</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jωm</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mo class=MathClass-open>(</mo>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(19)
</table>
<p>
   The sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   or transmission loss
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>TL</mi>
   </math>
   is given as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>TL</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>10</mn>
      <mi mathvariant=italic>log</mi>
      <mo class=MathClass-open>(</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <mi>T</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
               </mrow>
            </msup>
            <mn>2</mn>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </mfrac>
      <mo class=MathClass-close>)</mo>
   </math>
   , thus yielding the final expression
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi mathvariant=italic>TL</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≈</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>jωm</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(20)
</table>
<p>
   At higher frequencies
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ωm</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <mn>2</mn>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   , thus reducing the expression for transmission loss to the well-known mass law.
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi mathvariant=italic>TL</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≈</mo>
            <mn>20</mn>
            <mi mathvariant=italic>log</mi>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>ωm</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(21)
</table>
<h4 class=subsectionHead>Oblique incidence</h4>
<p>
   When the incident angle of the incoming sound wave is oblique, i.e. between 0 and 90 degrees, an effect of refraction should be considered for determination of transmission angle. There is a number of possibilities to define the oblique incidence problems and find the possible values of the pressure transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   and the pressure reflection coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   (total reflection, angle of intromission, and grazing incidence). However, this section will be restricted to the derivation of the mass law, assuming that medium 1 is the same as medium 2, and that the separating medium in between is a wall with a known mechanical impedance expressed in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kg</mi>
      <mo class=MathClass-bin>∕</mo>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   . The derivation will be made assuming only that the panel (wall) has a mass (surface density mass
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>
   ), while the stiffness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   and losses
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   will be neglected in the equations of motion due to very small values of wall velocity and displacement. The derivation will make use of the model shown in <a href="#" data-img-alt="Figure 5</a>. Lumped element model for sound transmission through a single-leaf construction at oblique incidence." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-5.svg>Figure 5</a>.
   <figure class=figure>
<p><img alt="Figure 5. Lumped element model for sound transmission through a single-leaf construction at oblique incidence." src=${config.ASSETS_URL}/05/17/theory/figure-5.svg><figcaption class=caption>Figure 5. Lumped element model for sound transmission through a single-leaf construction at oblique incidence.</figcaption></figure>
<p>The Newton law applied to an infinitesimally small surface element of the wall for arbitrary waveform of the incident sound wave yields:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>m</mi>
            <mover accent=true>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>u</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>˙</mo>
            </mover>
            <mi mathvariant=italic>dS</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>dS</mi>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>pt</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(22)
</table>
<p>
   For harmonic excitation and response, with the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jωt</mi>
         </mrow>
      </msup>
   </math>
   term, the equation above becomes
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>m</mi>
            <mover accent=true>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>˙</mo>
            </mover>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(23)
</table>
<p>The normal component of the wall velocity (assuming harmonic excitation) is given as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mi mathvariant=italic>cos</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(24)
</table>
<p>It is equal to the sum of the normal components of particle velocities in front of the panel
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>Ui</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Ur</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>cos𝜃</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mi mathvariant=italic>cos</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(25)
</table>
<p>
   With the pressure reflection coefficient defined as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   , the pressure transmission coefficient defined as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   , and the known relationships between the pressure transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   , power transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
   </math>
   , and the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   , the mass law for an oblique incidence angle
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   is given as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>T</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jωmcos𝜃</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mo class=MathClass-open>(</mo>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(26)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>τ</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <mi mathvariant=italic>ωm</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                              <mi mathvariant=italic>ρc</mi>
                           </mrow>
                        </mfrac>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi mathvariant=italic>co</mi>
                  <msup>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>𝜃</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(27)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-rel>|</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>cos𝜃</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>ρc</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(28)
</table>
<p>
   In the equations above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   is the acoustic wall impedance defined with surface mass
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>
   as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   =
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ΔP</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jωm</mi>
   </math>
   , assuming harmonic excitation.<br class=newline>
<p>
   If
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ωm</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi mathvariant=italic>πfm</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <mn>2</mn>
      <mi mathvariant=italic>ρc</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   , the sound reduction index is directly proportional with the surface mass of the panel.
<table class=equation>
   <tr>
      <td style="font-size: 11px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-rel>|</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jωm</mi>
            <mo class=MathClass-bin>∕</mo>
            <mo class=MathClass-open>(</mo>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>|</mo>
            <mo class=MathClass-rel>≈</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>πfm</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ρc</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(29)
</table>
<p>
   The mass law is often modified in practical resolution of building acoustics problems because the incident sound field is random or diffuse, i.e. the sound is incident on a wall or a panel at all possible angles.<br class=newline>For such situations, the sound reduction index for random incidence angle can be derived by integrating over all angles of incidence
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   ranging from
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∘</mo>
         </mrow>
      </msup>
   </math>
    to 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mn>90</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∘</mo>
         </mrow>
      </msup>
   </math>
   .<br class=newline>Random incidence means that the sound field on the source side of the partition is approximately diffuse. In such a sound field, the incident sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   on a wall surface can be found by integrating the sound intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   over the entire solid angle
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ψ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
   </math>
   , assuming that the sound really is coming from all directions. The transmitted sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   can then be found by multiplying the incident sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   with the power transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
   </math>
   . The incident sound power can be found by multiplying the random incidence intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   with the surface area of the wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   .
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>τ</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mo>∫</mo>
                     </mrow>
                     <mrow>
                        <mi>Ψ</mi>
                        <mo class=MathClass-rel>=</mo>
                        <mn>2</mn>
                        <mi>π</mi>
                     </mrow>
                  </msub>
                  <mi>τ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mi>S</mi>
                  <mstyle mathvariant=normal>
                     <mi>d</mi>
                  </mstyle>
                  <mi>Ψ</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mo>∫</mo>
                     </mrow>
                     <mrow>
                        <mi>Ψ</mi>
                        <mo class=MathClass-rel>=</mo>
                        <mn>2</mn>
                        <mi>π</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mi>S</mi>
                  <mstyle mathvariant=normal>
                     <mi>d</mi>
                  </mstyle>
                  <mi>Ψ</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(30)
</table>
<p>
   Therefore, the sound reduction index for random incidence
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   (measured either in a laboratory without flanking transmission or in-situ conditions with flanking transmission) can be expressed by mass law and the resulting sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   for normal incidence.
<table class=equation>
   <tr>
      <td style="font-size: 9px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>logτ</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mn>0</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>23</mn>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>6</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>4</mn>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(31)
</table>
<p>
   The expression for the random incidence sound reduction index is not a sufficiently accurate prediction of the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   . For typical values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   between 30 dB and 60 dB, the sound reduction index for random incidence
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   would be 8 dB to 11 dB lower than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   . The typical values of the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   obtained from measurements show it to be about 5 dB lower than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   . Therefore, the connection between the sound reduction indices for random and normal incidence is given empirically, as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>R</mi>
            <mo class=MathClass-rel>≈</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(32)
</table>
<p>
   This difference comes from the finite size of partitions, for which it is less likely that the sound waves will arrive at incident angles close to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mn>90</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∘</mo>
         </mrow>
      </msup>
   </math>
   (grazing incidence).<br class=newline>
<p>
   The calculated values of the sound reduction index are shown in <a href="#" data-img-alt="Figure 6. Comparison of the sound reduction index for normal, field and random incidence." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-6.svg>Figure 6</a> for different types of the incident sound field. The results are shown for a large size concrete wall with thickness equal to 300 mm and the surface mass equal to 690
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kg</mi>
      <mo class=MathClass-bin>∕</mo>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   . Resonance and coincidence effects are not included.
   <figure class=figure>
<p><img alt="Figure 6. Comparison of the sound reduction index for normal, field and random incidence." src=${config.ASSETS_URL}/05/17/theory/figure-6.svg><figcaption class=caption>Figure 6. Comparison of the sound reduction index for normal, field and random incidence.</figcaption></figure>
<h3 class=sectionHead>Coincidence effect</h3>
<p>
   Solid materials such as walls, aluminium or glass panels, etc. are capable of supporting shear as well as compression stress. As a result, <strong><span class=cmbx-10>shear </span><span class=cmbx-10>and torsional waves</span></strong> can propagate through such materials along with <strong><span class=cmbx-10>longitudinal waves</span></strong>. As a predictor of the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   , the mass law is further limited by the phenomena of coincidence and resonance. In thick walls or steel beams in large buildings, all three types of wave propagation (longitudinal, shear and bending) can be important in the audio frequency range of interest. In thin structures, from which wall panels are usually constructed, purely longitudinal waves propagation can be neglected. In the audible frequency range of interest, the propagation of sound through such kind of panels is determined primarily due to the excitation of bending waves, which are a combination of shear waves and compression waves (longitudinal waves). The transmission of sound through a panel occurs because the incident sound field forces the panel to move, and the panel radiates sound into the medium on the other side.<br class=newline>In the derivation of the mass law it is assumed that the entire panel moves as a whole, because the driving pressure (the pressure difference between the total pressure in front of the wall and behind it) is the same over the entire surface of the panel. When the angle of incidence is oblique, the pressure varies over the panel surface. Due to this phenomenon, the panel is set into flexural motion, as shown in <a href="#" data-img-alt="Figure 7. Bending deformation of a panel caused by a sound wave coming at an oblique angle of incidence." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-7.svg>Figure 7</a>. The panel thickness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>l</mi>
   </math>
   is much smaller than the wavelength of sound inside the panel itself. The wavefronts of peaks and troughs are labeled accordingly.
   <figure class=figure>
<p><img alt="Figure 7. Bending deformation of a panel caused by a sound wave coming at an oblique angle of incidence." src=${config.ASSETS_URL}/05/17/theory/figure-7.svg><figcaption class=caption>Figure 7. Bending deformation of a panel caused by a sound wave coming at an oblique angle of incidence.</figcaption></figure>
<p>
   The panel is deformed outward and inward by the pressure peaks and troughs. As the peaks and troughs move over the panel, they push the deformation through the panel, thus driving it into flexural (bending) wave motion. In <a href="#" data-img-alt="Figure 7. Bending deformation of a panel caused by a sound wave coming at an oblique angle of incidence." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-7.svg>Figure 7</a> it is visible that the flexural (bending) wave travels with a speed equal to the trace velocity (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>trace</mi>
         </mrow>
      </msub>
   </math>
   ) of the incident sound wave in air along the surface of the panel  [2].
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>trace</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>sin</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(33)
</table>
<p>
   In the equation above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   is the speed of sound in medium 1. In building acoustics, medium 1 is usually air.<br class=newline>
<p>
   The panel radiates the wave into the space behind it due to the transverse motion only if the propagation speed of the transverse wave in the panel (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>trace</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>bending</mi>
         </mrow>
      </msub>
   </math>
   ) is higher than the speed of sound in the air.<br class=newline>The propagation speed of the flexural (bending) waves is determined with the bending stiffness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>
   as the function of: plate thickness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>l</mi>
   </math>
   and Young’s modulus of elasticity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
   </math>
   , with the assumed value of the Poisson ratio
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ν</mi>
   </math>
   = 0.3 for most rigid materials.
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>B</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>E</mi>
                  <msup>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>12</mn>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>ν</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(34)
</table>
<p>
   The bending wave speed is derived by solving the Euler-Bernoully equation for an unloaded panel, i.e. when the panel is not subjected to any kind of sound field. The bending wave speed in plate
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   is given with bending stiffness and the surface mass of the panel
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>
   as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mroot>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>B</mi>
                        <msup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mroot>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(35)
</table>
<p>
   It can be seen that the bending wave speed increases with the square root of the excitation frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi mathvariant=italic>πf</mi>
   </math>
   . Consequently, for any panel subjected to shear stress there exists an excitation frequency at which the bending wave speed in the panel is equal to the speed of the sound wave in the surrounding medium. This frequency is called the
   <strong>
      <span class=cmbx-10>critical frequency</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   . At this particular frequency, the speeds of the airborne and solid-borne sound waves are the same. The critical frequency can be calculated as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(36)
</table>
<p>
   This is the lowest possible frequency at which coincidence effects can occur, and is defined for grazing incidence, i.e. for a sound wave travelling parallel to the panel, at the angle of incidence
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   =
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mn>90</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∘</mo>
         </mrow>
      </msup>
   </math>
   .<br class=newline>At other possible angles of incidence
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   smaller than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mn>90</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∘</mo>
         </mrow>
      </msup>
   </math>
   , the coincidence of airborne and solid-borne waves will occur at a different frequency. This frequency is the function of the incident angle
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   , and it is always higher than the critical frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   due to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>sin</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   term being smaller than 1. For a given panel and a given incidence angle, this frequency can be calculated as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>coinc</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>si</mi>
                  <msup>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(37)
</table>
<p>
   The coincidence effect leads to a large increase of the pressure transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   and the power transmission coefficient (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
   </math>
   ). Sound reduction index (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
      </msub>
   </math>
   is significantly reduced when this effect occurs. In building acoustics it is crucial to take this effect into account. The pressure transmission coefficient is expressed as
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>T</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mo class=MathClass-open>[</mo>
                  <mi mathvariant=italic>jωmcos</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>∕</mo>
                  <mo class=MathClass-open>(</mo>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-close>]</mo>
                  <mo class=MathClass-open>[</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <mi>f</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <msub>
                           <mrow>
                              <mi>f</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi mathvariant=italic>si</mi>
                  <msup>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-close>]</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(38)
</table>
<p>
   The angle-dependent sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
      </msub>
   </math>
   is expressed as
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>𝜃</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>20</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-rel>|</mo>
            <mi mathvariant=italic>cos</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>|</mo>
            <mo class=MathClass-bin>+</mo>
            <mn>20</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-rel>|</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi>f</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi mathvariant=italic>si</mi>
            <msup>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>|</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(39)
</table>
<p>
   The top chart in <a href="#" data-img-alt="Figure 8. Sound reduction index of a concrete wall (top) and a glass panel (bottom)." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-8.svg>Figure 8</a> shows the estimated frequency-dependent sound reduction index of a 4 m by 4 m concrete wall with the density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ρ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2400</mn>
      <mi mathvariant=italic>kg</mi>
      <mo class=MathClass-bin>∕</mo>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   , and the thickness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>180</mn>
      <mi mathvariant=italic>mm</mi>
   </math>
   , according to ISO 12354-1. The final result in any given one-third octave band is obtained through energetically averaging of values obtained at all considered frequencies that fall into that one-third octave band giving more weight to the lower value of sound reduction index.
   <figure class=figure>
<p><img alt="Figure 8. Sound reduction index of a concrete wall (top) and a glass panel (bottom)." src=${config.ASSETS_URL}/05/17/theory/figure-8.svg><figcaption class=caption>Figure 8. Sound reduction index of a concrete wall (top) and a glass panel (bottom).</figcaption></figure>
<p>
   The critical frequency is low (around 100 Hz), and dips at higher frequencies are not visible. It is visible that there are two frequency ranges distinguished by the one dip in the sound reduction curve. The dip occurs due to coincidence effect caused by bending waves in the structure which is different materials.<br class=newline>For light panels (glass, thin aluminium or steel plates), great differences can be observed in the transition frequencies between the frequency ranges defined above, so each panel should be considered separately, taking into account its size, thickness and the material it is made of.<br class=newline>Thick cement walls have a low critical frequency, but the critical frequency of thin glass panels will be in the kHz range. Increasing the thickness of the glass panel in order to increase the sound reduction index due to larger mass leads to the decrease of the critical frequency as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mi>h</mi>
   </math>
   . This means that the dip in the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   is shifted downwards into the frequency range important for sound insulation considerations. Therefore, the glass should not be more than 6-8 mm thick.<br class=newline>If there is a need for higher sound reduction index, heavier and more dampened glass panel is needed. In this case, laminated glass is a common solution that removes the dip in the sound reduction index in the coincidence region due to high damping provided by the lamination. High damping simply means that the panel is not prone to propagation of bending waves.<br class=newline>The successful design of a building structure with a high sound reduction index involves increasing the mass and putting the critical (resonant frequencies) either below (thick cement walls) or above (glass panels) the frequency range of interest.
<h3 class=sectionHead>Double-leaf construction</h3>
<p>
   A construction with two plates/panels set at a distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   is called a double-leaf construction. The gap between the plates can be filled with some absorbing material such as mineral wool.
   <figure class=figure>
<p><img alt="Figure 9. Lumped element model for sound transmission through a double-leaf construction at normal incidence." src=${config.ASSETS_URL}/05/17/theory/figure-9.svg><figcaption class=caption>Figure 9. Lumped element model for sound transmission through a double-leaf construction at normal incidence.</figcaption></figure>
<p>
   In the first approximation, it is assumed that the wall impedances are given only with their surface masses, i.e.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   . The losses and stiffness terms, as well as coincidence and resonance effects are neglected. By setting appropriate boundary conditions for pressure and particle velocities (as was done above for single-leaf constructions), and assuming that the walls are thin and that the gap is filled with air, the expression for the normal-incidence sound reduction index is yielded as:
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mo class=MathClass-rel>|</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(40)
</table>
<table class=equation>
   <tr>
      <td style="font-size: 7px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mo class=MathClass-rel>|</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>cos</mi>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kd</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mi>j</mi>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msubsup>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>sin</mi>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kd</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(41)
</table>
<p>If small terms are neglected, the expression for the normal-incidence sound reduction index with the cavity filled with air is given as:
<table class=equation>
   <tr>
      <td style="font-size: 5px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>[</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mfrac>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <mi mathvariant=italic>ρc</mi>
                     </mrow>
                  </mfrac>
                  <mi mathvariant=italic>sin</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mfrac>
                     <mrow>
                        <mi>ω</mi>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <mi mathvariant=italic>ρc</mi>
                     </mrow>
                  </mfrac>
                  <mi mathvariant=italic>cos</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>ρc</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
                  <mi mathvariant=italic>sin</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-close>]</mo>
         </math>
      <td class=eq-no>(42)
</table>
<p>
   The sound reduction index will have the minimum value when the last term in the equation above is equal to zero. This frequency at which this happens is called the resonance frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   . It can be calculated as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi mathvariant=italic>tan</mi>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kd</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>ρc</mi>
               </mrow>
               <mrow>
                  <mi>ω</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(43)
</table>
<p>
   If
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>
   , i.e. at low frequencies, we can assume that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>tan</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≈</mo>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>ωcd</mi>
   </math>
   , thus yielding a simplified equation for the resonance frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≈</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-open>(</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(44)
</table>
<p>
   If the approximation made above is not applied, many solutions of the equation for the resonance frequency can be found if the depth of the cavity is comparable to the wavelength, i.e. whenever the condition
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>nπ</mi>
   </math>
   is satisfied, where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   is a natural number. The dips in the sound reduction index will appear at resonance frequencies for which the condition
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>nλ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>
   is satisfied. The boundary between the low- and the high-frequency range is defined with the so-called crossover frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   , at which
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   .
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(45)
</table>
<p>
   The rather complex expression for the normal-incidence sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   can be simplified for different frequency ranges. In the frequency range
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   , i.e. below the fundamental resonance frequency, we obtain
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≈</mo>
            <mn>20</mn>
            <mi mathvariant=italic>log</mi>
            <mfrac>
               <mrow>
                  <mi>ω</mi>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>ρc</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(46)
</table>
<p>In this frequency range, the double-leaf construction behaves as a single-leaf structure with the total mass calculated as the sum of individual masses of the two leaves.<br class=newline>
<p>
   In the region between the resonance frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and the crossover frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   , the sound reduction index is given as
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≈</mo>
            <mn>20</mn>
            <mi mathvariant=italic>log</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msup>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>20</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mn>2</mn>
            <mi mathvariant=italic>kd</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(47)
</table>
<p>The sound insulation properties of both panels follow the mass law, and the corresponding sound reduction indices increase by 6 dB per doubling of frequency. The third term also increases at the same rate. Therefore, the sound reduction index of the entire structure increases by 18 dB per doubling of frequency.<br class=newline>
<p>
   In the frequency range above the crossover frequency, i.e. with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   , the width of cavity starts to be comparable to the wavelength, and in this case, the term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>sin</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be replaced with its maximum value, yielding the approximate expression
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≈</mo>
            <mn>20</mn>
            <mi mathvariant=italic>log</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msup>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>6</mn>
            <mi mathvariant=italic>dB</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(48)
</table>
<p>If the sound reduction indices of the two leaves are still governed by mass law (in the lower part of this frequency range), the overall sound reduction index increases by 12 dB per doubling of frequency. At higher frequencies, cavity resonances come into play, as well as plate resonances and coincidence effect of individual leaves.<br class=newline>
<p>
   The implementation of equations that describe the behaviour of double-leaf constructions regarding their sound insulation index is shown on an example in <a href="#" data-img-alt="Figure 10. Example of the sound reduction index of a double-leaf construction calculated using the exact expression, the approximated expression, and the approximation per frequency ranges." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-10.svg>Figure 10</a>. The calculation is made for a double-leaf wall made of two gypsum boards with the density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ρ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>840</mn>
      <mi mathvariant=italic>kg</mi>
      <mo class=MathClass-bin>∕</mo>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   and thickness of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>10</mn>
      <mi mathvariant=italic>mm</mi>
   </math>
   , and air cavity between them has a width of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>100</mn>
      <mi mathvariant=italic>mm</mi>
   </math>
   .<br class=newline>
   <figure class=figure>
<p><img alt="Figure 10. Example of the sound reduction index of a double-leaf construction calculated using the exact expression, the approximated expression, and the approximation per frequency ranges." src=${config.ASSETS_URL}/05/17/theory/figure-10.svg><figcaption class=caption>Figure 10. Example of the sound reduction index of a double-leaf construction calculated using the exact expression, the approximated expression, and the approximation per frequency ranges.</figcaption></figure>
<p>It is visible that the displayed approaches will yield different results even in this simplified situation, i.e. without taking into account the resonances of finite-sized plates and the coincidence effect.<br class=newline>
<p>All double-leaf constructions benefit from choosing different thickness of the two leaves. In this case, the dips in the sound reduction index caused by resonance are different for each leaf and are not as pronounced as they would be if two identical leaves were used.<br class=newline>
<p>If additional damping material is added into the cavity, the sound reduction index increases significantly. The constructions with additional panels are used in combination with medium or heavy-weight single walls. Heavy walls have a relatively low critical frequency and the sound radiation of resonant modes is more expressed in the sound reduction index. This resonant radiation can be decreased by adding a lightweight structure on one side of the wall.<br class=newline>
<p>If the air cavity is partially or fully filled with absorptive material, the losses introduced by that material should be included into the equation for the sound reduction index, as follows:
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>R</mi>
            <mo class=MathClass-rel>≈</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>[</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>K</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>K</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
                  <mi mathvariant=italic>cos</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
                  <mi>n</mi>
                  <msub>
                     <mrow>
                        <mi>K</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>K</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>sin</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msubsup>
               <mrow>
                  <mi>η</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-close>]</mo>
         </math>
      <td class=eq-no>(49)
</table>
<p>
   In the equation above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi mathvariant=italic>ρc</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi mathvariant=italic>ρc</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are normalized wall impedances of the two leaves, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>η</mi>
   </math>
   is the loss factor of the absorbing material that is significant in resonance. It should be noted that in this case the wave number
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi mathvariant=italic>πf</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   is given by knowing the speed of sound
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   characteristic for the longitudinal propagation of sound inside the material stuffed into the cavity. Furthermore,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   is the ratio of the impedance of the air outside the wall and the impedance of the material in the cavity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>ρc</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   .<br class=newline>
<p>
   The fundamental resonance frequency of a double-leaf structure with its cavity filled with absorbing material is given for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>
   as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≈</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                        </msub>
                        <msubsup>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-open>(</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(50)
</table>
<h3 class=sectionHead>Transmission loss of composite structures</h3>
<p>If a structure is not monolithic, but is composed of several elements, e.g. a concrete wall with windows and/or doors, a strict derivation of the transmission loss of such a structure is rather complicated. Instead, a simplified approach is used, assuming that the sound fields on both sides of the structure are diffuse and uncorrelated.<br class=newline>
<p>Using this approach, the total transmission of power through the structure is simply the sum of the individual power transmissions through different elements of the structure.<br class=newline>
<p>
   <a href="#" data-img-alt="Figure 11. With the definition of the sound reduction index of composite partitions." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-11.svg>Figure 11</a> shows an example of a complex structure that consists of a wall, a door, two windows and a ventilation grill. Each of these elements can be described with its surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   and the power transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   .<br class=newline>
   <figure class=figure>
<p><img alt="Figure 11. With the definition of the sound reduction index of composite partitions." src=${config.ASSETS_URL}/05/17/theory/figure-11.svg><figcaption class=caption>Figure 11. With the definition of the sound reduction index of composite partitions.</figcaption></figure>
<p>
   Assuming that the sound intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>inc</mi>
         </mrow>
      </msub>
   </math>
   incident on the entire structure with the total surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is constant, the total incident sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   on the partition is given as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>inc</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>S</mi>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>inc</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(51)
</table>
<p>
   The total sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   transmitted through the partition is given as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>inc</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>res</mi>
               </mrow>
            </msub>
            <mi>S</mi>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>inc</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(52)
</table>
<p>
   The power transmission coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>res</mi>
         </mrow>
      </msub>
   </math>
   of a composite structure is given as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>res</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(53)
</table>
<p>In terms of sound reduction index (transmission loss), the total sound reduction index of a composite structure is given as
<table class=equation>
   <tr>
      <td style="font-size: 11px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>total</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>0</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>1</mn>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(54)
</table>
<p>It is evident that the sound insulation performance of the structure as a whole will dominantly depend on the element with the lowest sound reduction index, even if the surface area of that element is small.<br class=newline>
<p>
   An example is shown in <a href="#" data-img-alt="Figure 12. The change of the total sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi>t</mi></mrow></msub></math> with the sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi mathvariant=italic>high</mi></mrow></msub></math> of the major portion of the wall with good sound insulation performance. The ”weak spot” of the wall has a fixed sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi mathvariant=italic>low</mi></mrow></msub></math> = 20 dB, and its size is expressed as a fraction of its surface area <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mi mathvariant=italic>low</mi></mrow></msub></math> in the total surface area of the wall <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mi mathvariant=italic>total</mi></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-12.svg>Figure 12</a>. A composite wall with the surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>total</mi>
         </mrow>
      </msub>
   </math>
   is made of two elements. The major part of the wall is made of material with the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>high</mi>
         </mrow>
      </msub>
   </math>
   that can change between 20 and 60 dB. A smaller part of the wall with the surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>low</mi>
         </mrow>
      </msub>
   </math>
   is made of material with the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>low</mi>
         </mrow>
      </msub>
   </math>
   fixed to 20 dB.<br class=newline>
<p>
   The chart in <a href="#" data-img-alt="Figure 12. The change of the total sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi>t</mi></mrow></msub></math> with the sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi mathvariant=italic>high</mi></mrow></msub></math> of the major portion of the wall with good sound insulation performance. The ”weak spot” of the wall has a fixed sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi mathvariant=italic>low</mi></mrow></msub></math> = 20 dB, and its size is expressed as a fraction of its surface area <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mi mathvariant=italic>low</mi></mrow></msub></math> in the total surface area of the wall <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mi mathvariant=italic>total</mi></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-12.svg>Figure 12</a> shows how the total sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   changes for the described structure, depending on the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>high</mi>
         </mrow>
      </msub>
   </math>
   that describes the major portion of the wall. The parameter on the chart is the fraction of the surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>low</mi>
         </mrow>
      </msub>
   </math>
   of the ”weaker” part of the wall in the total surface area of the wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>total</mi>
         </mrow>
      </msub>
   </math>
   , expressed in percent.<br class=newline>
<p>
   What can be concluded from this chart? The only way to preserve high sound insulation of the wall is not to have a ”weak spot” at all, as indicated by the uppermost line on the chart. On the other hand, if the surface area of the ”weak spot” takes up as little as 1 percent of the total surface area of the wall, the total sound insulation index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   is already significantly reduced. In this case, it reaches the value of 40 dB and cannot be increased beyond that even though the major portion of the wall has the sound reduction index up to 60 dB. It would not make any difference if this value was even higher. The ”weak spot”, i.e. the 1 percent of the total surface area with the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>low</mi>
         </mrow>
      </msub>
   </math>
   equal to only 20 dB dictates the sound insulation performance of the entire wall.
   <figure class=figure>
<p><img alt="Figure 12. The change of the total sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi>t</mi></mrow></msub></math> with the sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi mathvariant=italic>high</mi></mrow></msub></math> of the major portion of the wall with good sound insulation performance. The ”weak spot” of the wall has a fixed sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi mathvariant=italic>low</mi></mrow></msub></math> = 20 dB, and its size is expressed as a fraction of its surface area <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mi mathvariant=italic>low</mi></mrow></msub></math> in the total surface area of the wall <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mi mathvariant=italic>total</mi></mrow></msub></math>." src=${config.ASSETS_URL}/05/17/theory/figure-12.svg><figcaption class=caption>Figure 12. The change of the total sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi>t</mi></mrow></msub></math> with the sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi mathvariant=italic>high</mi></mrow></msub></math> of the major portion of the wall with good sound insulation performance. The ”weak spot” of the wall has a fixed sound reduction index <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi mathvariant=italic>low</mi></mrow></msub></math> = 20 dB, and its size is expressed as a fraction of its surface area <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mi mathvariant=italic>low</mi></mrow></msub></math> in the total surface area of the wall <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mi mathvariant=italic>total</mi></mrow></msub></math>.</figcaption></figure>
<p>
   A gap in a wall is a special example of a two-element partition [3] In the first approximation, with diffraction effects neglected, it can be assumed that the power transmission coefficient of the gap is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   . However, this is not valid for the entire frequency range of interest.<br class=newline>
<p>
   The total sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   of a wall with sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and surface
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   , and the gap with sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   =0 dB and surface
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>gap</mi>
         </mrow>
      </msub>
   </math>
   is given as
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>0</mn>
                  <mo class=MathClass-punc>,</mo>
                  <mn>1</mn>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>gap</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(55)
</table>
<p>If the surface area of the gap is much smaller than the surface area of the wall, the approximate formula can be used
<table class=equation>
   <tr>
      <td style="font-size: 13px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>≈</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>0</mn>
                  <mo class=MathClass-punc>,</mo>
                  <mn>1</mn>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>gap</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(56)
</table>
<p>
   In this case, the total surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is assumed to be equal to the surface area of the wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   . <a href="#" data-img-alt="Figure 13. Sound reduction index of a wall with a gap." data-img-src=${config.ASSETS_URL}/05/17/theory/figure-13.svg>Figure 13</a> shows how the total sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   changes with the sound reduction index of a wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   for different sizes of the gap expressed as the ratio
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>gap</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi>S</mi>
   </math>
   .
   <figure class=figure>
<p><img alt="Figure 13. Sound reduction index of a wall with a gap." src=${config.ASSETS_URL}/05/17/theory/figure-13.svg><figcaption class=caption><span class=id>Figure 13. Sound reduction index of a wall with a gap.</figcaption></figure>
<h3 class=sectionHead>Final remarks</h3>
<p>There are a lot of formulas that describe the sound reduction index in different frequency regions. The most important part of the frequency range is the mass-controlled region, where the single-number value of the sound reduction index is estimated. In the low-frequency range (up to 100 Hz), the resonance effects in plates of finite dimensions significantly reduce the sound reduction index, and special care should be taken to deal with low-frequency noise sources. In the higher frequency range (above kHz), the coincidence effect occurs due to the appearance of flexural waves, which significantly reduces the sound reduction index.
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
   <dt>R
   <dd>Sound Reduction Index
   <dt>TL
   <dd>Transmission Loss
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Hansen2018 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>David.A Bies, Collin H Hansen, and Carl Q. Howard. <span class=cmti-10>Engineering Noise </span><span class=cmti-10>COntrol</span>. 5. ed., [repr.] CRC Press. 6000 Broken Sound Parkway NW,Suite 300 Boca Raton: Taylor and Francis, 2018. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9781498724067.
   <dt id=X0-Blackstock2000 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>David Blackstock. <span class=cmti-10>Sound Insulation in Buildings</span>. 1. ed., [repr.] CRC Press. John Wiley and Sons, 2000. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 0-471-31979-1.
   <dt id=X0-Hopkins2007 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Hopkins Carl. <span class=cmti-10>Sound Insulation</span>. 1. ed., [repr.] Routledge. New York: Taylor and Francis, 2007. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0-7506-6526-1.
   <dt id=X0-Rindel2018 class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p>Jens H. Rindel. <span class=cmti-10>Sound Insulation in Buildings</span>. 1. ed., [repr.] CRC Press. 6000 Broken Sound Parkway NW,Suite 300 Boca Raton: Taylor and Francis, 2018. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9781498700429.
</dl>`;

export default theory;
